let newCount: number = 0, changeCount: number = 0, moveCount: number = 0;
export const resetCounts = function(): void { newCount = changeCount = moveCount = 0; };
export const getCounts = function(): { new: number, changed: number, moved: number } {
    return { new: newCount, changed: changeCount, moved: moveCount };
};
export const clearBody = function(): void {
    document.body.childNodes.length = 0;
};

// A minimal stand-in for CSSStyleDeclaration: a plain string bag (so `style[prop]` keeps working)
// plus the setProperty/getPropertyValue/removeProperty methods needed for custom properties.
// The methods are non-enumerable so they don't leak into `Object.keys()`/`for..in` iteration.
function makeStyle(): Record<string, string> {
    const s: Record<string, string> = {};
    Object.defineProperties(s, {
        setProperty: { value(k: string, v: string) { s[k] = v; changeCount++; } },
        removeProperty: { value(k: string) { delete s[k]; changeCount++; } },
        getPropertyValue: { value(k: string) { return s[k] ?? ""; } },
    });
    return s;
}

class Node {
    parentNode: Element | null = null;
    childNodes: Node[] = [];
    
    get nextSibling(): Node | undefined {
        return this.getSibling(+1);
    }
    
    get previousSibling(): Node | undefined {
        return this.getSibling(-1);
    }
    
    getSibling(delta: number): Node | undefined {
        if (!this.parentNode) return undefined;
        let siblings = this.parentNode.childNodes;
        let idx = siblings.indexOf(this);
        if (idx < 0) throw new Error("not part of siblings!?");
        return siblings[idx + delta];
    }
}

class Element extends Node {
    tagName: string;
    namespaceURI?: string;
    // Tag name of the element an `innerHTML` fragment was parsed against; `_`-prefixed so
    // it stays out of `toString()`. Only set on emulated-html nodes.
    _parseContext?: string;
    _style: Record<string, string> = makeStyle();
    attrs: Record<string, string> = {};
    events: Record<string, Set<Function>> = {};
    _classList?: {
        add: (...names: string[]) => void;
        remove: (...names: string[]) => void;
        toggle: (name: string, force?: boolean) => void;
        contains: (name: string) => boolean;
    };
    
    constructor(tag: string, namespaceURI?: string) {
        super();
        this.tagName = tag;
        this.namespaceURI = namespaceURI;
        this.childNodes = [];
        newCount++;
    }
    
    appendChild(node: Node): void {
        this.insertBefore(node, null);
    }
    
    insertBefore(node: Node, ref: Node | null): void {
        if (node.parentNode) node.parentNode.removeChild(node);
        else changeCount++;
        node.parentNode = this;
        if (ref) {
            let idx = this.childNodes.indexOf(ref);
            if (idx < 0) throw new Error("non-existing ref node");
            this.childNodes.splice(idx, 0, node);
        } else {
            this.childNodes.push(node);
        }
    }
    
    removeChild(node: Node): void {
        let idx = this.childNodes.indexOf(node);
        if (idx < 0) throw new Error("no such child");
        this.childNodes.splice(idx, 1);
        node.parentNode = null;
        changeCount++;
    }

    // Atomic-move emulation. The real browser API also preserves element state (focus,
    // selection, running animations); here we just reposition within childNodes and count
    // the move. Stricter than the spec (which allows reparenting of connected nodes), so
    // misuse in the library shows up as a test failure.
    moveBefore(node: Node, ref: Node | null): void {
        if (node.parentNode !== this) throw new Error("moveBefore: node is not a child");
        if (node === ref) throw new Error("moveBefore: node and ref are the same");
        this.childNodes.splice(this.childNodes.indexOf(node), 1);
        if (ref) {
            let idx = this.childNodes.indexOf(ref);
            if (idx < 0) throw new Error("moveBefore: non-existing ref node");
            this.childNodes.splice(idx, 0, node);
        } else {
            this.childNodes.push(node);
        }
        moveCount++;
    }
    
    remove(): void {
        this.parentNode?.removeChild(this);
    }

    getAnimations(_options?: {subtree?: boolean}): {finished: Promise<unknown>}[] {
        // Without CSS we can't know whether a class change starts a transition. The
        // default (one never-finishing animation) makes removal wait for aberdeen's
        // 3-second backstop, the timing the pre-existing destroy tests assume.
        return animationsMock ? animationsMock() : [{finished: new Promise(() => {})}];
    }

    replaceChild(newNode: Node, oldNode: Node): void {
        this.insertBefore(newNode, oldNode);
        this.removeChild(oldNode);
    }
    
    setAttribute(k: string, v: any): void {
        if (v == null || v === '') delete this.attrs[k];
        else this.attrs[k] = '' + v;
        changeCount++;
    }
    
    getAttribute(k: string): string | undefined {
        return this.attrs[k];
    }
    
    removeAttribute(k: string): void {
        delete this.attrs[k];
        changeCount++;
    }
    
    get classList(): {
        add: (...names: string[]) => void;
        remove: (...names: string[]) => void;
        toggle: (name: string, force?: boolean) => void;
        contains: (name: string) => boolean;
    } {
        return this._classList || (this._classList = {
            add: (...names: string[]) => {
                let set = this._getClassSet();
                for(const name of names) set.add(name);
                this._setClassSet(set);
            },
            remove: (...names: string[]) => {
                let set = this._getClassSet();
                for(const name of names) set.delete(name);
                this._setClassSet(set);
            },
            toggle: (name: string, force?: boolean) => {
                if (force === true || (force !== false && !this._getClassSet().has(name))) this._classList!.add(name);
                else this._classList!.remove(name);
            },
            contains: (name: string) => this._getClassSet().has(name),
        });
    }
    
    get parentElement(): Element | undefined {
        if (this.parentNode instanceof Element) return this.parentNode;
        return undefined;
    }
    
    _getClassSet(): Set<string> {
        return new Set(this.attrs.class ? this.attrs.class.split(' ') : []);
    }
    
    _setClassSet(map: Set<string>): void {
        this.setAttribute('class', Array.from(map).sort().join(' ') || undefined);
    }
    
    get firstChild(): Node | undefined {
        return this.childNodes[0];
    }
    
    get lastChild(): Node | undefined {
        return this.childNodes[this.childNodes.length - 1];
    }
    
    set textContent(text: string) {
        this.childNodes = [new TextNode(text)];
    }
    
    get textContent() {
        let text = '';
        for(let child of this.childNodes) {
            if (child instanceof TextNode) {
                text += child.textContent;
            }
        }
        return text;
    }
    
    querySelectorAll(selector: string): Element[] {
        const results: Element[] = [];
        this._querySelectorAllRecursive(selector, results);
        return results;
    }
    
    private _querySelectorAllRecursive(selector: string, results: Element[]): void {
        for (const child of this.childNodes) {
            if (child instanceof Element) {
                if (child.tagName === selector) {
                    results.push(child);
                }
                child._querySelectorAllRecursive(selector, results);
            }
        }
    }
    
    set innerHTML(html: string) {
        this.childNodes = [];
        if (html) {
            // A real parser interprets the fragment relative to this element; we can't emulate that,
            // but we do record the context (tag + namespace) so tests can assert it's the right one.
            let n = new Element('fake-emulated-html', this.namespaceURI);
            n._parseContext = this.tagName;
            n.textContent = html;
            this.appendChild(n);
        }
    }
    
    set style(val: string) {
        if (val !== '') throw new Error("non-empty style string cannot be emulated");
        this._style = makeStyle();
        changeCount++;
    }

    get style(): Record<string, string> {
        for(let k in this._style) {
            if (this._style[k] === '') delete this._style[k];
        }
        return this._style;
    }
    
    set className(v: string) {
        this.attrs.class = v;
        changeCount++;
    }
    
    get offsetHeight(): number {
        return 20;
    }
    
    get offsetWidth(): number {
        return 200;
    }
    
    get scrollTop(): number {
        return this._scrollTop || 0;
    }
    
    set scrollTop(value: number) {
        this._scrollTop = value;
    }
    
    get scrollLeft(): number {
        return this._scrollLeft || 0;
    }
    
    set scrollLeft(value: number) {
        this._scrollLeft = value;
    }
    
    public _scrollTop?: number;
    public _scrollLeft?: number;
    
    toString(): string {
        let arr: string[] = [];
        for(let [symbol, items, toKebab] of [['=', this.attrs, false], ['->', this, false], [':', this.style, true]] as [string, Record<string, any>, boolean][]) {
            for(let key of Object.keys(items).sort()) {
                const outputKey = toKebab ? key.replace(/[A-Z]/g, l => `-${l.toLowerCase()}`) : key;
                const prefix = outputKey + symbol;
                if (key[0] === '_' || IGNORE_OUTPUT.has(prefix)) continue;
                let value = '' + items[key];
                if (value.indexOf(" ") >= 0 || value.indexOf("}") >= 0 || !value.length) value = JSON.stringify(value);
                arr.push(prefix + value);
            }
        }
        for(let child of this.childNodes) arr.push(child.toString());
        
        const cls = this.attrs['class'] ? "." + this.attrs['class'].replace(/ /g, '.') : '';
        return this.tagName + cls + (arr.length ? `{${arr.join(' ')}}` : '');
    }
    
    addEventListener(name: string, func: Function): void {
        this.events[name] = this.events[name] || new Set();
        this.events[name].add(func);
        changeCount++;
    }
    
    removeEventListener(name: string, func: Function): void {
        if (this.events[name]) {
            if (this.events[name].delete(func)) {
                changeCount++;
            }
        }
    }
    
    event(info: string | { type: string; [key: string]: any }): void {
        if (typeof info === 'string') info = {type: info};
        let type = info.type;
        info.target = this;
        info.preventDefault = function() {};
        info.stopPropagation = function() { info.stopped = true; };
        let node: Element | null = this;
        while(node && !info.stopped) {
            let funcs = node.events[type];
            if (funcs) {
                for(let func of funcs) {
                    func.call(node, info);
                }
            }
            node = node.parentNode;
        }
    }
    
    getElementById(id: string): Element | undefined {
        if (this.attrs.id === id) return this;
        for(let child of this.childNodes) {
            if (child instanceof Element) {
                let el = child.getElementById(id);
                if (el) return el;
            }
        }
        return undefined;
    }
}



const document = {
    createElement: (tag: string) => new Element(tag, 'http://www.w3.org/1999/xhtml'),
    createElementNS: (namespaceURI: string, tag: string) => new Element(tag, namespaceURI),
    createTextNode: (text: string) => new TextNode(text),
    head: new Element('head', 'http://www.w3.org/1999/xhtml'),
    body: new Element('body', 'http://www.w3.org/1999/xhtml')
};

// Browser history and location simulation
class FakeLocation {
    private _href: string = 'http://localhost/';
    
    get href(): string { return this._href; }
    set href(value: string) { 
        if (value.startsWith('/')) {
            // Relative path - convert to absolute URL
            const currentUrl = new URL(this._href);
            this._href = `${currentUrl.protocol}//${currentUrl.host}${value}`;
        } else {
            this._href = value;
        }
    }
    
    get host(): string {
        const url = new URL(this._href);
        return url.host;
    }
    
    get pathname(): string { 
        const url = new URL(this._href);
        return url.pathname;
    }
    set pathname(value: string) {
        const url = new URL(this._href);
        url.pathname = value;
        this._href = url.href;
    }
    
    get search(): string {
        const url = new URL(this._href);
        return url.search;
    }
    set search(value: string) {
        const url = new URL(this._href);
        url.search = value;
        this._href = url.href;
    }
    
    get hash(): string {
        const url = new URL(this._href);
        return url.hash;
    }
    set hash(value: string) {
        const url = new URL(this._href);
        url.hash = value;
        this._href = url.href;
    }
}

class FakeHistory {
    private _state: any = null;
    private _entries: Array<{url: string, state: any}> = [];
    private _index: number = 0;
    
    constructor() {
        // Initialize with current location
        this._entries = [{url: location.href, state: null}];
    }
    
    get state(): any {
        return this._state;
    }
    
    pushState(state: any, title: string, url: string): void {
        // Remove any entries after current index (like real browser behavior)
        this._entries = this._entries.slice(0, this._index + 1);
        
        // Add new entry
        this._entries.push({url, state});
        this._index = this._entries.length - 1;
        this._state = state;
        
        // Update location
        location.href = url;
    }
    
    replaceState(state: any, title: string, url: string): void {
        // state = JSON.parse(JSON.stringify(state)); // clone state
        this._entries[this._index] = {url, state};
        this._state = state;
        
        // Update location
        location.href = url;
    }
    
    go(delta: number): void {
        const newIndex = this._index + delta;
        if (newIndex >= 0 && newIndex < this._entries.length) {
            this._index = newIndex;
            const entry = this._entries[this._index];
            this._state = entry.state;
            location.href = entry.url;
            
            // Trigger popstate event asynchronously
            setTimeout(() => {
                const event = {
                    type: 'popstate',
                    state: this._state,
                    target: window,
                    preventDefault: () => {},
                    stopPropagation: () => {}
                };
                window.dispatchEvent(event);
            }, 0);
        }
    }
    
    back(): void {
        this.go(-1);
    }
    
    forward(): void {
        this.go(1);
    }
    
    // Test helper methods
    _reset(): void {
        this._state = null;
        this._entries = [{url: location.href, state: null}];
        this._index = 0;
    }
    
    _getLength(): number {
        return this._entries.length;
    }
}

class FakeWindow {
    private _listeners: Record<string, Set<Function>> = {};
    
    addEventListener(type: string, listener: Function): void {
        if (!this._listeners[type]) {
            this._listeners[type] = new Set();
        }
        this._listeners[type].add(listener);
    }
    
    removeEventListener(type: string, listener: Function): void {
        if (this._listeners[type]) {
            this._listeners[type].delete(listener);
        }
    }
    
    dispatchEvent(event: any): void {
        const listeners = this._listeners[event.type];
        if (listeners) {
            for (const listener of listeners) {
                listener.call(this, event);
            }
        }
    }
    
    matchMedia(query: string): MediaQueryList {
        if (!mediaQueries.has(query)) {
            mediaQueries.set(query, new MediaQueryList(query));
        }
        return mediaQueries.get(query)!;
    }
    
    // Test helper
    _reset(): void {
        this._listeners = {};
    }
}

const location = new FakeLocation();
const history = new FakeHistory();
const window = new FakeWindow();

// Reset function for tests
export const resetBrowserState = function(): void {
    location.href = 'http://localhost/';
    history._reset();
    // Don't reset window listeners as they're registered by the modules being tested
};

/**
 * Override what `Element.getAnimations()` returns (only `finished` is consulted).
 * Call without arguments to restore the never-finishing default.
 */
export function mockAnimations(mock?: () => {finished: Promise<unknown>}[]): void {
    animationsMock = mock;
}
let animationsMock: (() => {finished: Promise<unknown>}[]) | undefined;

type TimeoutItem = {
    func: () => void | Promise<void>;
    time: number;
    realId: ReturnType<typeof global.setTimeout>;
};

let timeouts: TimeoutItem[] = [];
let currentTime: number = 0;
let deferredErrors: any[] = [];

const realSetTimeout = global.setTimeout;
const realClearTimeout = global.clearTimeout;

function fireItem(item: TimeoutItem) {
    const idx = timeouts.indexOf(item);
    if (idx < 0) return; // Already fired by passTime or another path
    timeouts.splice(idx, 1);
    if (item.time > currentTime) currentTime = item.time;
    item.func();
}

const setTimeout = function(func: () => void, time: number): void {
    const item: TimeoutItem = { func, time: time + currentTime, realId: undefined! };
    item.realId = realSetTimeout(() => {
        try { fireItem(item); }
        catch (e) { deferredErrors.push(e); }
    }, time);
    timeouts.push(item);
};

const queueMicrotask = function(func: () => void): void {
    const item: TimeoutItem = { func, time: 0, realId: undefined! };
    item.realId = realSetTimeout(() => {
        try { fireItem(item); }
        catch (e) { deferredErrors.push(e); }
    }, 0);
    timeouts.push(item);
};

// Fast-forward fake time, firing all timeouts up to `ms` ms ahead (default 0).
// Awaits async timer callbacks so microtasks and chained timers complete too.
// Rethrows any errors from timeouts that fired via real timers in the background.
export const passTime = async function(ms: number = 0): Promise<void> {
    let targetTime = currentTime + ms;
    while(true) {
        await new Promise(resolve => realSetTimeout(resolve, 0));
        
        // Rethrow errors from background-fired timeouts
        if (deferredErrors.length) {
            const err = deferredErrors.shift();
            throw err;
        }
        
        if (!timeouts.length) {
            // Async I/O (e.g. database commits) may still be in flight, about to schedule
            // new timeouts. Retry a few real event-loop ticks before giving up.
            let settled = false;
            for (let r = 0; r < 5 && !settled; r++) {
                await new Promise(resolve => realSetTimeout(resolve, 0));
                settled = timeouts.length > 0;
            }
            if (!settled) break;
        }
        
        // Find the earliest timeout
        let smallestIdx = 0;
        for(let idx = 1; idx < timeouts.length; idx++) {
            if (timeouts[idx].time < timeouts[smallestIdx].time) smallestIdx = idx;
        }
        let item = timeouts[smallestIdx];
        
        if (item.time > targetTime) break;
        
        // Fire early, cancel real timer
        timeouts.splice(smallestIdx, 1);
        realClearTimeout(item.realId);
        if (item.time > currentTime) currentTime = item.time;
        await item.func();
    }
    currentTime = targetTime;
};

const IGNORE_OUTPUT = new Set("tagName-> attrs-> events-> childNodes-> parentNode-> class= namespaceURI->".split(" "));

class TextNode extends Node {
    textContent: string;
    
    constructor(textContent: string) {
        super();
        this.textContent = '' + textContent;
        newCount++;
    }
    
    toString(): string {
        return JSON.stringify(this.textContent);
    }
    
    remove() {
        this.parentNode?.removeChild(this);
    }
}

function getComputedStyle(el: Element): Record<string, string> {
    return el._style;
}

// MediaQueryList mock for testing
class MediaQueryList {
    matches: boolean;
    media: string;
    private listeners: Set<(event: any) => void> = new Set();
    
    constructor(query: string) {
        this.media = query;
        // Check if there's a preset value, otherwise default to light mode
        const preset = mediaQueryPresets.get(query);
        this.matches = preset !== undefined ? preset : (query.includes('dark') ? false : true);
    }
    
    addEventListener(type: string, listener: (event: any) => void) {
        if (type === 'change') {
            this.listeners.add(listener);
        }
    }
    
    removeEventListener(type: string, listener: (event: any) => void) {
        if (type === 'change') {
            this.listeners.delete(listener);
        }
    }
    
    // Test helper to simulate media query changes
    _trigger(matches: boolean) {
        this.matches = matches;
        const event = { matches, media: this.media };
        for (const listener of this.listeners) {
            listener(event);
        }
    }
}

const mediaQueries = new Map<string, MediaQueryList>();
const mediaQueryPresets = new Map<string, boolean>();

// Set media query value and trigger change event if already initialized
export function setMediaQuery(query: string, matches: boolean) {
    mediaQueryPresets.set(query, matches);
    // If already created, update it and trigger change
    if (mediaQueries.has(query)) {
        mediaQueries.get(query)!._trigger(matches);
    }
}

// Bun lacks `Temporal`, so install a mock. Each type uses a private brand so that
// calling a method on a proxied instance throws, matching the real behavior.
const Temporal: Record<string, new (...args: any[]) => object> = {};
for (const name of "Duration Instant PlainDate PlainDateTime PlainMonthDay PlainTime PlainYearMonth ZonedDateTime".split(" ")) {
    Temporal[name] = class {
        #brand = true;
        toString() {
            this.#brand; // throws if `this` is a proxy rather than the real instance
            return `[Temporal.${name}]`;
        }
    };
}


const globals = {Node, Element, TextNode, document, window, setTimeout, queueMicrotask, getComputedStyle, location, history, URLSearchParams, Temporal};
Object.assign(global, globals);
Object.assign(window, globals);
